/**
 * Marqeta Card Issuance Service
 * Handles Marqeta Issuing API integration for virtual and physical cards
 * Trust Marketplace - Payment Account Management
 */

import { getWeaveinApiUrl } from '@/src/config';

export interface MarqetaCardRequest {
  memberId: number;
  cardType: 'VIRTUAL' | 'PHYSICAL';
  cardholderName: string;
  loadAmount: number;
  spendingLimits?: {
    daily?: number;
    weekly?: number;
    monthly?: number;
  };
  allowedCategories?: string[]; // MCC codes
  restrictedCategories?: string[]; // MCC codes
}

export interface MarqetaCardResponse {
  id: number;
  card_id: string; // Our database ID
  external_card_id: string; // Marqeta card token
  card_number_last4: string;
  card_network: string;
  card_type: string;
  cardholder_name: string;
  status: string;
  current_balance: number;
  available_balance: number;
  expiration_date: string;
  created_at: string;
}

export interface CardTransaction {
  id: number;
  transaction_id: string;
  card_id: number;
  transaction_type: string;
  transaction_amount: number;
  merchant_name?: string;
  merchant_category_code?: string;
  status: string;
  transaction_date: string;
  balance_after: number;
}

class MarqetaCardIssuanceService {
  private baseUrl = getWeaveinApiUrl('/api');
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Issue a new card to a member
   */
  async issueCard(request: MarqetaCardRequest): Promise<MarqetaCardResponse> {
    try {
      // CORRECTED ENDPOINT: POST /members/{member_id}/issued-cards
      const response = await fetch(`${this.baseUrl}/members/${request.memberId}/issued-cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          card_type: request.cardType,
          cardholder_name: request.cardholderName,
          initial_load_amount: request.loadAmount,
          spending_limits: request.spendingLimits,
          allowed_merchant_categories: request.allowedCategories,
          restricted_merchant_categories: request.restrictedCategories
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to issue card');
      }

      return await response.json();
    } catch (error) {
      console.error('Error issuing card:', error);
      throw error;
    }
  }

  /**
   * Load funds onto a card
   */
  async loadCard(cardId: number, amount: number): Promise<MarqetaCardResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/issued-cards/${cardId}/load?amount=${amount}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to load card');
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading card:', error);
      throw error;
    }
  }

  /**
   * Activate a card
   */
  async activateCard(cardId: number): Promise<MarqetaCardResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/issued-cards/${cardId}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to activate card');
      }

      return await response.json();
    } catch (error) {
      console.error('Error activating card:', error);
      throw error;
    }
  }

  /**
   * Suspend a card
   */
  async suspendCard(cardId: number, reason: string): Promise<MarqetaCardResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/issued-cards/${cardId}/suspend?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to suspend card');
      }

      return await response.json();
    } catch (error) {
      console.error('Error suspending card:', error);
      throw error;
    }
  }

  /**
   * Get cards for a member
   */
  async getMemberCards(memberId: number, status?: string): Promise<MarqetaCardResponse[]> {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);

      const response = await fetch(`${this.baseUrl}/members/${memberId}/issued-cards?${params}`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch cards');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching cards:', error);
      throw error;
    }
  }

  /**
   * Get card transactions
   */
  async getCardTransactions(cardId: number): Promise<CardTransaction[]> {
    try {
      const response = await fetch(`${this.baseUrl}/issued-cards/${cardId}/transactions`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch transactions');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Update card spending controls
   */
  async updateSpendingControls(
    cardId: number,
    controls: {
      daily_limit?: number;
      transaction_limit?: number;
      allowed_categories?: string[];
      restricted_categories?: string[];
    }
  ): Promise<MarqetaCardResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/issued-cards/${cardId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(controls)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update controls');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating spending controls:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const marqetaCardService = new MarqetaCardIssuanceService();

// Export merchant category codes helper
export const MERCHANT_CATEGORIES = {
  GROCERY: ['5411', '5422', '5441', '5451', '5462'],
  GAS: ['5541', '5542'],
  RESTAURANTS: ['5812', '5813', '5814'],
  PHARMACY: ['5912', '5122'],
  HEALTHCARE: ['8011', '8021', '8031', '8041', '8042', '8043', '8049', '8050', '8062', '8071', '8099'],
  TRANSPORTATION: ['4111', '4112', '4121', '4131', '4784', '4789'],
  UTILITIES: ['4812', '4814', '4821', '4899', '4900'],
  RETAIL: ['5300', '5311', '5331', '5399', '5411', '5511', '5611', '5621', '5631', '5641', '5651', '5661', '5691', '5699', '5712', '5713', '5714', '5718', '5719', '5722', '5732', '5733', '5734', '5735', '5812', '5813', '5814', '5912', '5921', '5931', '5932', '5933', '5935', '5937', '5940', '5941', '5942', '5943', '5944', '5945', '5946', '5947', '5948', '5949', '5950', '5960', '5961', '5962', '5963', '5964', '5965', '5966', '5967', '5968', '5969', '5970', '5971', '5972', '5973', '5975', '5976', '5977', '5978', '5983', '5992', '5993', '5994', '5995', '5996', '5997', '5998', '5999'],
  CASH_WITHDRAWAL: ['6010', '6011'],
  GAMBLING: ['7800', '7801', '7802', '7995'],
  ALCOHOL: ['5921']
};

export default marqetaCardService;
